/**
 * The Overview tab (masterplan credit-limits-v3, A5, A7): the customers of Dolder-Bigler in the ClimGrid, started on the
 * active customers (balance or open orders), switchable to all; the row menu (right-click, menu key) opens open
 * invoices, open orders, sales, comments and the mail to the salesperson. The two kept components, the order list
 * (`TransactionDrillDownModal`) and the comments (`CreditLimitComments`), are used as they are.
 *
 * A7 (Roman's review, E30, E31): utilization and preview as banded badges and a tinted row, the key-figure tiles above
 * the grid, and the page filters (search, salesperson, «My customers», the tile) kept in the URL so a link reproduces
 * the list. The rules live in `overviewFilters.ts`; the grid's own funnels stay inside the grid.
 *
 * B4 (cell edit, E7, E11, E14, E19): the limit cell of a customer the person may change (salesperson, BT manager,
 * Finance) is marked and opens with Enter, F2, a digit, a double-click or the pencil (`grid/cellEdit.ts`); Enter in
 * the open cell continues to `LimitRequestDialog` (reason, chain preview), Esc discards. A sent request locks the
 * cell at once and the overview reloads, so «Last request» shows the server's status.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { ClimGrid } from '../grid';
import {
    CLOSED, isSameLimit, keyFromEvent, openCell, parseLimitInput, setDraft as setCellDraft, transition,
    type CellEditState,
} from '../grid/cellEdit';
import { TransactionDrillDownModal } from '../TransactionDrillDownModal';
import {
    DEFAULT_NEAR_LIMIT_PCT,
    type CreditLimitCallerRoles, type CreditLimitRequestDto, type DrillDownType, type OpenRequestSummary, type OverviewData,
    type OverviewRow, type OverviewScope,
} from '../../../types/creditLimits';
import { STATUS_LABEL } from '../requests/requestRules';
import { CommentsDialog } from './CommentsDialog';
import { EmailDialog } from './EmailDialog';
import { LimitEditor } from './LimitCell';
import { LimitRequestDialog } from './LimitRequestDialog';
import { limitEditBlock, withLocalRequests } from './limitEdit';
import { OpenInvoicesDialog } from './OpenInvoicesDialog';
import { buildOverviewMenu, canWriteComments, overviewColumns, type LimitEditColumn } from './overviewColumns';
import {
    EMPTY_FILTERS, TILES, applyPageFilters, applyTile, myCustomersOf, parseFilterParams, rowBandClass, salesManagerOptions,
    searchTextOf, tileStats, writeFilterParams, type OverviewFilterState, type TileKey,
} from './overviewFilters';
import { OverviewTiles } from './OverviewTiles';

type Dialog =
    | { kind: 'invoices'; row: OverviewRow }
    | { kind: 'drill'; row: OverviewRow; type: DrillDownType }
    | { kind: 'comments'; row: OverviewRow }
    | { kind: 'email'; row: OverviewRow }
    | { kind: 'limit'; row: OverviewRow; amountChf: number };

interface Props {
    overview: OverviewData | null;
    roles: CreditLimitCallerRoles | null;
    scope: OverviewScope;
    onScopeChange: (scope: OverviewScope) => void;
    loading: boolean;
    error: string | null;
    lookupError: string | null;
    onRetry: () => void;
}

/** How long the search waits after the last key before it filters and writes the URL. */
export const SEARCH_DEBOUNCE_MS = 250;

const rowKey = (r: OverviewRow) => r.customerCode;
const NO_ROWS: readonly OverviewRow[] = [];
/** The grid's top in the viewport plus the bottom gutter, with the tiles and the filter line above it (kit.css §5). */
const PANEL_STYLE = { '--tbl-offset': '400px' } as CSSProperties;

export function OverviewTab({ overview, roles, scope, onScopeChange, loading, error, lookupError, onRetry }: Props) {
    const [dialog, setDialog] = useState<Dialog | null>(null);
    const [params, setParams] = useSearchParams();
    const filters = useMemo(() => parseFilterParams(params), [params]);
    const nearLimitPct = overview?.nearLimitPct ?? DEFAULT_NEAR_LIMIT_PCT;
    const mine = useMemo(() => myCustomersOf(roles), [roles]);

    const update = useCallback((patch: Partial<OverviewFilterState>) => {
        setParams(prev => writeFilterParams(prev, { ...parseFilterParams(prev), ...patch }), { replace: true });
    }, [setParams]);

    // ---- search: typed into a draft, written to the URL after a pause; a change of the URL from outside (reset, back)
    // comes back into the field, the echo of our own write does not.
    const [draft, setDraft] = useState(filters.q);
    const written = useRef(filters.q);
    useEffect(() => {
        if (filters.q === written.current) return;
        written.current = filters.q;
        setDraft(filters.q);
    }, [filters.q]);
    useEffect(() => {
        const target = draft.trim() === '' ? '' : draft;
        if (target === filters.q) return;
        const t = window.setTimeout(() => { written.current = target; update({ q: target }); }, SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(t);
    }, [draft, filters.q, update]);

    // ---- the cell edit (B4). Requests sent from here lock their cell until the reloaded overview carries them.
    const [localRequests, setLocalRequests] = useState<ReadonlyMap<string, OpenRequestSummary>>(() => new Map());
    useEffect(() => { setLocalRequests(new Map()); }, [overview]);
    const [cell, setCell] = useState<CellEditState>(CLOSED);
    const [cellError, setCellError] = useState<string | null>(null);
    const [notice, setNotice] = useState<CreditLimitRequestDto | null>(null);
    const requestsAvailable = overview?.requestsAvailable ?? false;
    const gridRef = useRef<HTMLDivElement | null>(null);

    // ---- the rows: page filters, then the tile
    const rows = useMemo(() => withLocalRequests(overview?.rows ?? NO_ROWS, localRequests), [overview, localRequests]);
    const searchIndex = useMemo(() => new Map(rows.map(r => [r, searchTextOf(r)] as const)), [rows]);
    const searchOf = useCallback((r: OverviewRow) => searchIndex.get(r) ?? searchTextOf(r), [searchIndex]);
    const filtered = useMemo(
        () => applyPageFilters(rows, { ...EMPTY_FILTERS, q: filters.q, sm: filters.sm, mine: filters.mine }, mine, searchOf),
        [rows, filters.q, filters.sm, filters.mine, mine, searchOf],
    );
    const stats = useMemo(() => tileStats(filtered, nearLimitPct), [filtered, nearLimitPct]);
    const shown = useMemo(() => applyTile(filtered, filters.band, nearLimitPct), [filtered, filters.band, nearLimitPct]);
    const blockOf = useCallback((r: OverviewRow) => limitEditBlock(r, roles, requestsAvailable), [roles, requestsAvailable]);
    const editable = useCallback((r: OverviewRow) => blockOf(r) === null, [blockOf]);

    const focusRow = useCallback((key: string) => {
        window.setTimeout(() => {
            const tr = [...(gridRef.current?.querySelectorAll<HTMLTableRowElement>('tbody tr[data-key]') ?? [])].find(t => t.dataset.key === key);
            tr?.focus();
        }, 0);
    }, []);
    const startEdit = useCallback((r: OverviewRow, initial = '') => {
        setCellError(null);
        setCell(s => openCell(s, r.customerCode, blockOf(r) !== null, initial));
    }, [blockOf]);
    // The editor reads the open cell through a ref, so the columns (and the grid's column state) stay the same object
    // while the person types; the grid re-renders with this component and draws the new draft.
    const cellRef = useRef({ cell, cellError });
    cellRef.current = { cell, cellError };
    const cancelEdit = useCallback(() => {
        // A blur after the cell already closed (Enter went on to the dialog) must not take the focus back to the row.
        const open = cellRef.current.cell.editing;
        if (!open) return;
        setCell(CLOSED);
        setCellError(null);
        focusRow(open.rowKey);
    }, [focusRow]);
    const commitEdit = useCallback((r: OverviewRow) => {
        const draft = cellRef.current.cell.editing?.draft ?? '';
        const parsed = parseLimitInput(draft);
        if (!parsed.ok) { setCellError(parsed.error); return; }
        if (isSameLimit(parsed.value, r.creditLimitChf)) { setCellError('This is the current limit.'); return; }
        setCell(CLOSED);
        setCellError(null);
        setDialog({ kind: 'limit', row: r, amountChf: parsed.value });
    }, []);
    const limitEdit = useMemo<LimitEditColumn>(() => ({
        editable,
        locked: r => blockOf(r) === 'open_request',
        onEdit: r => startEdit(r),
        renderEditor: r => (
            <LimitEditor
                row={r}
                draft={cellRef.current.cell.editing?.draft ?? ''}
                error={cellRef.current.cellError}
                onDraft={d => { setCellError(null); setCell(s => setCellDraft(s, d)); }}
                onCommit={() => commitEdit(r)}
                onCancel={cancelEdit}
            />
        ),
    }), [editable, blockOf, startEdit, commitEdit, cancelEdit]);
    const columns = useMemo(() => overviewColumns(nearLimitPct, limitEdit), [nearLimitPct, limitEdit]);

    // Enter and a digit on a focused row open its limit cell (F2 and the double-click come through the grid).
    const onGridKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (cell.editing) return;
        const tr = e.target as HTMLElement;
        if (tr.tagName !== 'TR' || !tr.dataset.key) return;
        const k = keyFromEvent(e);
        if (!k) return;
        const r = rows.find(x => x.customerCode === tr.dataset.key);
        if (!r) return;
        const next = transition(cell, k, { rowKey: r.customerCode, locked: !editable(r) });
        if (next.effect.type === 'open') {
            e.preventDefault();
            setCellError(null);
            setCell(next.state);
        }
    };

    const onRequestCreated = (created: CreditLimitRequestDto, row: OverviewRow) => {
        setLocalRequests(prev => new Map(prev).set(row.customerCode, {
            id: created.id, status: created.status, nextApproverEmail: created.nextApproverEmail, nextApproverRole: created.nextApproverRole,
        }));
        setNotice(created);
        setDialog(null);
        focusRow(row.customerCode);
        onRetry();
    };
    const openRequestTab = (id: number) => {
        setParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('tab', 'requests');
            next.set('request', String(id));
            return next;
        });
    };
    const rowClassName = useCallback((r: OverviewRow) => rowBandClass(r, nearLimitPct), [nearLimitPct]);
    const smOptions = useMemo(() => salesManagerOptions(rows, filters.sm), [rows, filters.sm]);

    // «Reset all filters» also clears the grid's funnels: they live inside the grid, so it is drawn afresh.
    const [gridGeneration, setGridGeneration] = useState(0);
    const resetAll = () => {
        written.current = '';
        setDraft('');
        update({ ...EMPTY_FILTERS });
        setGridGeneration(n => n + 1);
    };

    const menu = useMemo(() => (row: OverviewRow) => buildOverviewMenu(row, {
        invoices: r => setDialog({ kind: 'invoices', row: r }),
        orders: r => setDialog({ kind: 'drill', row: r, type: 'openOrders' }),
        sales: r => setDialog({ kind: 'drill', row: r, type: 'salesTransactions' }),
        comments: r => setDialog({ kind: 'comments', row: r }),
        email: r => setDialog({ kind: 'email', row: r }),
        // E21: the mail to the salesperson only for a person who may write on the customer (same rule as comments).
        canEmail: r => canWriteComments(r, roles),
    }), [roles]);

    const close = () => setDialog(null);

    // The rows on screen belong to `overview.scope`; `scope` is what was asked for. They differ while the other scope
    // loads or after it failed, so the pressed button follows the rows, never the request.
    const shownScope = overview?.scope ?? scope;
    const switching = loading && overview !== null && overview.scope !== scope;

    const mineAvailable = mine.kind !== 'none';
    const mineTitle = mine.kind === 'all'
        ? 'Finance: all customers'
        : mine.kind === 'names' ? `Customers of ${[...mine.names].join(', ')}` : undefined;

    const chips: { key: string; label: string; remove: () => void }[] = [];
    if (filters.q.trim()) chips.push({ key: 'q', label: `Search: ${filters.q.trim()}`, remove: () => { written.current = ''; setDraft(''); update({ q: '' }); } });
    if (filters.sm) chips.push({ key: 'sm', label: `Salesperson: ${filters.sm}`, remove: () => update({ sm: null }) });
    if (filters.mine && mineAvailable) chips.push({ key: 'mine', label: 'My customers', remove: () => update({ mine: false }) });
    if (filters.band) chips.push({ key: 'band', label: TILES.find(t => t.key === filters.band)!.label, remove: () => update({ band: null }) });

    const toggleTile = (key: TileKey) => update({ band: filters.band === key ? null : key });

    const toolbar = (
        <>
            <label className="fsearch">
                <Search className="i sm" aria-hidden="true" />
                <input
                    type="search"
                    className="input sm"
                    aria-label="Search customer, number, salesperson or payment terms"
                    placeholder="Search"
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                />
            </label>
            <select
                className="input sm fselect"
                aria-label="Salesperson"
                value={filters.sm ?? ''}
                onChange={e => update({ sm: e.target.value === '' ? null : e.target.value })}
            >
                <option value="">All salespeople</option>
                {smOptions.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            {mineAvailable ? (
                <button type="button" className="btn secondary sm" aria-pressed={filters.mine} title={mineTitle} onClick={() => update({ mine: !filters.mine })}>
                    My customers
                </button>
            ) : null}
            <div role="group" aria-label="Customers shown" className="toolbar">
                <button type="button" className="btn secondary sm" aria-pressed={shownScope === 'active'} onClick={() => onScopeChange('active')} disabled={loading}>
                    Active customers
                </button>
                <button type="button" className="btn secondary sm" aria-pressed={shownScope === 'all'} onClick={() => onScopeChange('all')} disabled={loading}>
                    All customers
                </button>
            </div>
        </>
    );

    return (
        <div className="clim-panel" style={PANEL_STYLE}>
            {error && (
                <div className="banner error" role="alert">
                    <p>{error}</p>
                    <div className="actions"><button type="button" className="btn secondary sm" onClick={onRetry}>Retry</button></div>
                </div>
            )}
            {lookupError && (
                <div className="banner attention" role="alert">
                    <p>Your roles could not be loaded ({lookupError}). You can read everything; commenting and «My customers» are switched off until they load.</p>
                </div>
            )}
            {overview && !overview.requestsAvailable && (
                <div className="banner info" role="status">
                    <p>Limit requests are not available yet; the last column stays empty.</p>
                </div>
            )}

            {loading && !overview && <p className="muted" role="status">Loading customers…</p>}
            {switching && (
                <p className="muted" role="status">
                    Loading {scope === 'all' ? 'all' : 'active'} customers… the list below still shows the {shownScope === 'all' ? 'all' : 'active'} customers.
                </p>
            )}

            {overview && <OverviewTiles stats={stats} active={filters.band} nearLimitPct={nearLimitPct} onToggle={toggleTile} />}

            {chips.length > 0 && (
                <div className="fchips" role="group" aria-label="Active filters">
                    {chips.map(c => (
                        <span key={c.key} className="fchip">
                            <span className="fchip-text">{c.label}</span>
                            <button type="button" className="fchip-x" aria-label={`Remove filter ${c.label}`} onClick={c.remove}>
                                <X className="i sm" aria-hidden="true" />
                            </button>
                        </span>
                    ))}
                    <button type="button" className="lnk" onClick={resetAll}>Reset all filters</button>
                </div>
            )}

            {notice && (
                <div className="banner info" role="status">
                    <p>
                        Request #{notice.id} for {notice.customerName ?? notice.customerCode} sent: {STATUS_LABEL[notice.status] ?? notice.status}.
                        {' '}The limit stays locked until the request is decided or withdrawn.
                    </p>
                    <div className="actions">
                        <button type="button" className="btn secondary sm" onClick={() => openRequestTab(notice.id)}>Open request</button>
                        <button type="button" className="btn quiet sm" onClick={() => setNotice(null)}>Dismiss</button>
                    </div>
                </div>
            )}

            {overview && (
                <div ref={gridRef} onKeyDown={onGridKeyDown} style={{ display: 'contents' }}>
                    <ClimGrid<OverviewRow>
                        key={gridGeneration}
                        gridId="credit-limits-overview"
                        caption="Customers of Dolder-Bigler"
                        columns={columns}
                        rows={shown}
                        rowKey={rowKey}
                        defaultSort={{ key: 'utilization', direction: 'desc' }}
                        rowMenu={menu}
                        rowClassName={rowClassName}
                        toolbar={toolbar}
                        editing={cell.editing ? { rowKey: cell.editing.rowKey, columnKey: 'limit' } : null}
                        onEditRequest={(r, key) => { if (key === 'limit') startEdit(r); }}
                        labels={{
                            count: shownCount => `${shownCount.toLocaleString('de-CH')} of ${rows.length.toLocaleString('de-CH')} customers`,
                            empty: 'No customers match the filters.',
                        }}
                    />
                </div>
            )}

            {dialog?.kind === 'limit' && (
                <LimitRequestDialog
                    row={dialog.row}
                    amountChf={dialog.amountChf}
                    requesterEmail={roles?.email ?? ''}
                    onClose={() => { const key = dialog.row.customerCode; setDialog(null); focusRow(key); }}
                    onCreated={created => onRequestCreated(created, dialog.row)}
                />
            )}

            {dialog?.kind === 'invoices' && <OpenInvoicesDialog row={dialog.row} onClose={close} />}
            {dialog?.kind === 'drill' && (
                <TransactionDrillDownModal
                    customerCode={dialog.row.customerCode}
                    customerName={dialog.row.customerName ?? dialog.row.customerCode}
                    drillDownType={dialog.type}
                    onClose={close}
                />
            )}
            {dialog?.kind === 'comments' && <CommentsDialog row={dialog.row} canEdit={canWriteComments(dialog.row, roles)} onClose={close} />}
            {dialog?.kind === 'email' && <EmailDialog row={dialog.row} onClose={close} />}
        </div>
    );
}
